import type { ApiBody, ApiQuery, ApiResponse, MutationResult } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import type { PartnerRepository } from './repository';
import type {
  IpAllowlistEntry,
  OneTimeSecretResult,
  PartnerCredential,
  PartnerListQuery,
} from './types';

type PartnerListResponse = ApiResponse<'/api/v1/partners', 'get'>;
type PartnerResponse = ApiResponse<'/api/v1/partners/{id}', 'get'>;
type CredentialListResponse = ApiResponse<'/api/v1/partners/{id}/credentials', 'get'>;
type CredentialResponse = ApiResponse<
  '/api/v1/partners/{id}/credentials/{credentialId}/expiry',
  'post'
>;
type CredentialIssueResponse = ApiResponse<'/api/v1/partners/{id}/credentials', 'post'>;
type IpListResponse = ApiResponse<'/api/v1/partners/{id}/ip-allowlist', 'get'>;
type IpResponse = ApiResponse<'/api/v1/partners/{id}/ip-allowlist/{entryId}', 'patch'>;

export type IdempotencyKeyFactory = () => string;

export function createRealPartnerRepository(
  apiClient: ApiClient,
  accessToken: string | null,
  createIdempotencyKey: IdempotencyKeyFactory = defaultIdempotencyKey,
): PartnerRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };

  return {
    async list(query, signal) {
      const result = await apiClient.get<PartnerListResponse>('/api/v1/partners', {
        ...auth,
        query: toListQuery(query),
        signal,
      });
      return {
        items: result.data.data,
        total: result.data.pagination.total,
        requestId: result.requestId,
      };
    },
    async get(partnerId, signal) {
      const result = await apiClient.get<PartnerResponse>(partnerPath(partnerId), {
        ...auth,
        signal,
      });
      return result.data.data;
    },
    async create(input) {
      const result = await apiClient.post<ApiResponse<'/api/v1/partners', 'post'>>(
        '/api/v1/partners',
        input,
        auth,
      );
      return mutation(result);
    },
    async update(partnerId, input) {
      const result = await apiClient.patch<ApiResponse<'/api/v1/partners/{id}', 'patch'>>(
        partnerPath(partnerId),
        input,
        auth,
      );
      return mutation(result);
    },
    async changeStatus(partnerId, input) {
      const result = await apiClient.post<ApiResponse<'/api/v1/partners/{id}/status', 'post'>>(
        `${partnerPath(partnerId)}/status`,
        input,
        auth,
      );
      return mutation(result);
    },
    listCredentials(partnerId, signal) {
      return collectPages<CredentialListResponse, PartnerCredential>(signal, (page) =>
        apiClient.get<CredentialListResponse>(`${partnerPath(partnerId)}/credentials`, {
          ...auth,
          query: { page, pageSize: 100, sort: 'createdAt', order: 'desc' },
          signal,
        }),
      );
    },
    async issueCredential(partnerId, input) {
      const result = await apiClient.post<CredentialIssueResponse>(
        `${partnerPath(partnerId)}/credentials`,
        input,
        { ...auth, idempotencyKey: freshKey(createIdempotencyKey) },
      );
      return oneTimeSecret(result);
    },
    async rotateCredential(partnerId, credentialId, input) {
      const result = await apiClient.post<CredentialIssueResponse>(
        `${partnerPath(partnerId)}/credentials/${segment(credentialId)}/rotate`,
        input,
        { ...auth, idempotencyKey: freshKey(createIdempotencyKey) },
      );
      return oneTimeSecret(result);
    },
    async changeCredentialExpiry(partnerId, credentialId, input) {
      const result = await apiClient.post<CredentialResponse>(
        `${partnerPath(partnerId)}/credentials/${segment(credentialId)}/expiry`,
        input,
        auth,
      );
      return mutation(result);
    },
    async revokeCredential(partnerId, credentialId, input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/partners/{id}/credentials/{credentialId}/revoke', 'post'>
      >(`${partnerPath(partnerId)}/credentials/${segment(credentialId)}/revoke`, input, auth);
      return mutation(result);
    },
    listIpAllowlist(partnerId, signal) {
      return collectPages<IpListResponse, IpAllowlistEntry>(signal, (page) =>
        apiClient.get<IpListResponse>(`${partnerPath(partnerId)}/ip-allowlist`, {
          ...auth,
          query: { page, pageSize: 100, sort: 'updatedAt', order: 'desc' },
          signal,
        }),
      );
    },
    async createIpAllowlistEntry(partnerId, input) {
      if (input.network === undefined || input.network.trim().length === 0) {
        throw new RangeError('IP allowlist network is required');
      }
      const body: ApiBody<'/api/v1/partners/{id}/ip-allowlist', 'post'> = {
        network: input.network,
        description: input.description,
        reason: input.reason,
      };
      const result = await apiClient.post<
        ApiResponse<'/api/v1/partners/{id}/ip-allowlist', 'post'>
      >(`${partnerPath(partnerId)}/ip-allowlist`, body, auth);
      return mutation(result);
    },
    async updateIpAllowlistEntry(partnerId, entryId, input) {
      const body: ApiBody<'/api/v1/partners/{id}/ip-allowlist/{entryId}', 'patch'> = {
        description: input.description,
        reason: input.reason,
      };
      const result = await apiClient.patch<IpResponse>(
        `${partnerPath(partnerId)}/ip-allowlist/${segment(entryId)}`,
        body,
        auth,
      );
      return mutation(result);
    },
    async changeIpAllowlistStatus(partnerId, entryId, input) {
      const body: ApiBody<'/api/v1/partners/{id}/ip-allowlist/{entryId}/status', 'post'> = {
        status: toPartnerStatus(input.status),
        reason: input.reason,
      };
      const result = await apiClient.post<
        ApiResponse<'/api/v1/partners/{id}/ip-allowlist/{entryId}/status', 'post'>
      >(`${partnerPath(partnerId)}/ip-allowlist/${segment(entryId)}/status`, body, auth);
      return mutation(result);
    },
  };
}

function toListQuery(query: PartnerListQuery): ApiQuery<'/api/v1/partners', 'get'> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    sort: query.sort,
    order: query.order,
    ...(query.filters.code.length === 0 ? {} : { code: query.filters.code }),
    ...(query.filters.name.length === 0 ? {} : { name: query.filters.name }),
    ...(query.filters.status === '' ? {} : { status: query.filters.status }),
    ...(query.filters.currency.length === 0 ? {} : { currency: query.filters.currency }),
  };
}

function mutation<T>(result: {
  readonly data: { readonly data: T };
  readonly requestId: string | null;
}): MutationResult<T> {
  return { data: result.data.data, requestId: result.requestId };
}

function oneTimeSecret(result: {
  readonly data: CredentialIssueResponse;
  readonly requestId: string | null;
}): OneTimeSecretResult {
  return {
    credential: result.data.data.credential,
    secret: result.data.data.secret,
    requestId: result.requestId,
  };
}

async function collectPages<
  Response extends { readonly data: readonly Item[]; readonly pagination: Pagination },
  Item,
>(
  signal: AbortSignal,
  load: (page: number) => Promise<{ readonly data: Response }>,
): Promise<readonly Item[]> {
  const items: Item[] = [];
  let page = 1;
  let lastPage = 1;
  let total = Number.POSITIVE_INFINITY;

  do {
    throwIfAborted(signal);
    const result = await load(page);
    throwIfAborted(signal);
    items.push(...result.data.data);
    total = result.data.pagination.total;
    lastPage = Math.max(page, result.data.pagination.totalPages);
    page += 1;
  } while (page <= lastPage && items.length < total);

  return items;
}

interface Pagination {
  readonly total: number;
  readonly totalPages: number;
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException('Partner request aborted', 'AbortError');
}

function partnerPath(partnerId: string): string {
  return `/api/v1/partners/${segment(partnerId)}`;
}

function segment(value: string): string {
  return encodeURIComponent(value);
}

function freshKey(factory: IdempotencyKeyFactory): string {
  const key = factory().trim();
  if (key.length === 0) throw new Error('Idempotency key factory returned an empty value');
  return key;
}

function defaultIdempotencyKey(): string {
  return crypto.randomUUID();
}

function toPartnerStatus(status: string): 'ACTIVE' | 'SUSPENDED' | 'CLOSED' {
  if (status === 'ACTIVE' || status === 'SUSPENDED' || status === 'CLOSED') return status;
  throw new RangeError(`Unsupported Partner status: ${status}`);
}
